import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { ModeStandbyOutlined, PersonOff, People } from "@mui/icons-material";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getCompanyTableColumns } from "../../pages/superadmin/constants";
import { SubscriptionService } from "../../services/subscription.service";
import { AdminService, CompanyService } from "../../services";
import { derivePdfColumns, getPrintableRows } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const CompanyList = ({ totalCount = 0, setTotalCount = () => {} }) => {
  const [companies, setCompanies] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [plans, setPlans] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "companies",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (companyFilters = {}) => {
      setLoading(true);
      try {
        const { data: companyResponse } = await CompanyService.getCompanyList(companyFilters);
        const adminIds = Array.from(new Set(companyResponse?.rows?.map((item) => item.ownerUserId)));
        const companyIds = companyResponse?.rows?.map((item) => item.id);

        if (adminIds.length > 0) {
          const { data: adminResponse } = await AdminService.getUserList({
            ids: adminIds.join(","),
            limit: adminIds.length,
            type: "ADMIN",
          });
          setAdmins(adminResponse?.rows);
        }

        // Fetch plans for all companies
        if (companyIds && companyIds.length > 0) {
          const { data: planResponse } = await SubscriptionService.getAllPlans({ companyIds: companyIds.join(",") });
          setPlans(planResponse?.rows || []);
        } else {
          setPlans([]);
        }

        setCompanies(companyResponse?.rows);
        setTotalCount(companyResponse?.count);
      } catch (error) {
        toast.error("Failed to fetch companies");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleSortChange = (model) => {
    if (model.length === 0) return;
    setSortModel(model);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={"Companies"} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Companies",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/companies/${params.row.id}`);
  };

  const handleEditCompany = async (companyId) => navigate(`/companies/edit/${companyId}`);

  const openDeleteModal = (companyId) => {
    setDeleteModal({
      isOpen: true,
      id: companyId,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      id: null,
    });
  };

  const handleDelete = async () => {
    try {
      await CompanyService.deleteCompany(deleteModal.id);
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        active: selectedFilter,
        name: searchText,
      });
      toast.success("Company deleted successfully");
    } catch (error) {
      toast.error("Failed to delete company");
      console.error("🚀 ~ handleDelete ~ error:", error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (companyData, actionType) => {
    switch (actionType) {
      case "edit":
        handleEditCompany(companyData?.id);
        break;
      case "delete":
        openDeleteModal(companyData?.id);
        break;
      default:
        return;
    }
  };

  const columnHeaders = getCompanyTableColumns(handleActions);

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const rows = companies
    ? companies.map((company, index) => {
        const admin = admins.find((admin) => admin.id === company.ownerUserId);
        const plan = plans.find((p) => p.companyId === company.id);
        return {
          id: company.id,
          sl: index + 1 + paginationState.page * paginationState.pageSize,
          createdAt: moment(company.createdAt).format("DD MMM YYYY"),
          name: company.name,
          primaryPhone: company?.primaryPhone,
          admin: admin ? `${admin.firstName} ${admin.lastName}` : "N/A",
          place: company?.primaryAddress?.locality || "—",
          planDetails: plan || null,
        };
      })
    : [];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          // Search data
          placeholder="Search company"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={handleSortChange}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this company?"
      />
    </>
  );
};
